"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import {
    Avancer,
    Curseur,
    Ecran,
    Liste,
    Question,
    Recommencer,
    Resultat,
    Segments,
    euros,
    pourcent,
    remplir,
    useParcours,
} from "./Journey";
import { quandQuitterLaMicro } from "@/lib/tools/leavingMicro";
import { expliquerQuitterMicro } from "@/lib/tools/explanations";
import { MontantExplicable, PanneauExplication, phraseAvecMontants, useExplications } from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Quand quitter la micro-entreprise ?
 *
 * LA RÉPONSE QU'ON N'ATTEND PAS : CE N'EST PAS UN CHIFFRE D'AFFAIRES. On croit
 * qu'on quitte la micro « quand on gagne bien ». Or à chiffre d'affaires égal,
 * c'est la part des dépenses qui décide : la micro ne déduit rien, et ses
 * cotisations portent sur tout ce qu'on encaisse. L'écran donne donc d'abord
 * le montant de dépenses au-delà duquel changer rapporte mille euros par an,
 * puis la courbe de ce seuil quand le chiffre d'affaires monte, avec le point
 * du lecteur posé dessus.
 *
 * MÊMES CALCULS QUE LE COMPARATEUR DE RÉGIMES, qui détaille les quatre
 * régimes à un chiffre d'affaires donné : les deux écrans ne peuvent pas se
 * contredire, et celui-ci renvoie vers l'autre pour le détail.
 *
 * TOUT RESTE DANS LE NAVIGATEUR : aucun appel réseau, aucun stockage.
 */

const PHASES = {
    activite: 0,
    chiffreAffaires: 1,
    charges: 1,
    foyer: 2,
    autresRevenus: 2,
    resultat: 3,
};

const L = {
    fr: {
        phases: ["Activité", "Chiffres", "Foyer"],
        retour: "Retour",
        activite: {
            titre: "Quelle est votre activité ?",
            aide: "C'est elle qui fixe l'abattement de votre micro-entreprise, et le taux de vos cotisations.",
            choix: [
                {
                    cle: "services",
                    icone: "case-minimalistic-linear",
                    titre: "Prestations de services",
                    detail: "Artisanat, bâtiment, coiffure, transport. Abattement de 50 %.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Activité libérale",
                    detail: "Conseil, développement, design, formation. Abattement de 34 %.",
                },
                {
                    cle: "vente",
                    icone: "shop-linear",
                    titre: "Vente de marchandises",
                    detail: "Achat-revente, hébergement. Abattement de 71 %.",
                },
            ],
        },
        chiffreAffaires: {
            titre: "Combien encaissez-vous dans l'année ?",
            aide: "Hors taxes, tout ce qui arrive sur le compte de votre activité.",
            libelle: "Chiffre d'affaires annuel",
            suivant: "Continuer",
        },
        charges: {
            titre: "Et combien dépensez-vous pour travailler ?",
            aide: "Matériel, logiciels, publicité, sous-traitance, déplacements, local : tout ce que la micro ne vous laisse pas déduire.",
            libelle: "Dépenses professionnelles annuelles",
            part: "C'est {part} de votre chiffre d'affaires.",
            suivant: "Continuer",
        },
        foyer: {
            titre: "Qui compose votre foyer fiscal ?",
            aide: "Votre impôt dépend de tout votre foyer, et il pèse dans la comparaison.",
            situation: "Situation",
            seul: "Seul",
            couple: "En couple",
            enfants: "Enfants à charge",
            suivant: "Continuer",
        },
        autresRevenus: {
            titre: "Y a-t-il d'autres revenus dans votre foyer ?",
            aide: "Un salaire, une pension, les revenus de votre conjoint. Nous ne comptons que l'impôt en plus que votre activité vous fait payer.",
            libelle: "Autres revenus imposables du foyer",
            aucun: "Aucun autre revenu",
            suivant: "Voir quand quitter la micro",
        },
        resultat: {
            titre: "Quitter la micro",
            dejaTitre: "Quitter la micro vous rapporterait déjà {gain} par an",
            dejaDetail:
                "À {ca} de chiffre d'affaires, vous gagnez mille euros à changer dès {seuil} de dépenses, et vous en êtes à {charges}. Le régime qui vous laisse le plus, c'est {regime}.",
            resterTitre: "Restez en micro tant que vos dépenses ne dépassent pas {seuil}",
            resterDetail:
                "C'est votre seuil à {ca} de chiffre d'affaires, soit {part} de ce que vous encaissez. Aujourd'hui, vous en êtes à {charges}.",
            jamaisTitre: "La micro reste votre meilleur régime",
            jamaisDetail:
                "À {ca} de chiffre d'affaires, même si vous dépensiez tout ce que vous encaissez, aucun autre régime ne vous rapporterait mille euros de plus.",
            avenirSeuil:
                "Si vos dépenses gardent la même part quand votre activité grandit, quitter la micro commencera à vous rapporter à partir de {seuil} de chiffre d'affaires.",
            avenirJamais:
                "Si vos dépenses gardent la même part quand votre activité grandit, la micro restera devant jusqu'à son plafond de {plafond}.",
            regimes: {
                reel: "l'entreprise individuelle au réel",
                eurl: "l'EURL",
                sasu: "la SASU",
            },
            courbe: {
                titre: "Votre seuil de dépenses, selon votre chiffre d'affaires",
                aide: "Au-dessus de la courbe, quitter la micro vous rapporte au moins mille euros par an. En dessous, restez. Le point, c'est vous.",
                au: "Quitter rapporte",
                sous: "Rester",
                tva: "TVA",
            },
            reperes: {
                titre: "À ne pas confondre avec votre seuil",
                abattement:
                    "Votre abattement forfaitaire vaut {abattement}. Votre seuil arrive bien avant, parce qu'en micro, vous payez vos cotisations sur tout ce que vous encaissez, dépenses comprises.",
                tva: "La TVA arrive à {tva} de chiffre d'affaires, quel que soit votre régime : elle ne vous dit pas quand quitter la micro.",
                plafond:
                    "Le plafond de la micro est de {plafond}, et il faut le dépasser deux années de suite pour en sortir.",
            },
            detailLien: "Voir le détail des quatre régimes",
            societeLien: "Rémunération ou dividendes, en société",
            hypotheses: {
                titre: "Pour ce calcul, nous avons supposé que :",
                items: [
                    "vos dépenses gardent la même part de votre chiffre d'affaires quand il augmente ;",
                    "en société, vous vous versez tout ce qu'elle peut vous verser, sans dividendes ;",
                    "les honoraires du comptable et les taxes pour frais de chambre ne sont pas comptés ;",
                    "l'impôt compté est celui que votre activité ajoute à votre foyer, pas tout l'impôt du foyer.",
                ],
            },
            avertissement:
                "Ces montants sont indicatifs. Avant de changer de régime, parlez-en à un expert-comptable : le changement touche aussi votre protection sociale.",
            recommencer: "Refaire le calcul",
        },
    },
    en: {
        phases: ["Activity", "Figures", "Household"],
        retour: "Back",
        activite: {
            titre: "What is your activity?",
            aide: "It sets the allowance of your micro-entreprise, and your contribution rate.",
            choix: [
                {
                    cle: "services",
                    icone: "case-minimalistic-linear",
                    titre: "Services",
                    detail: "Crafts, building, hairdressing, transport. 50% allowance.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Liberal profession",
                    detail: "Consulting, development, design, training. 34% allowance.",
                },
                {
                    cle: "vente",
                    icone: "shop-linear",
                    titre: "Sale of goods",
                    detail: "Buying and reselling, accommodation. 71% allowance.",
                },
            ],
        },
        chiffreAffaires: {
            titre: "How much do you collect in a year?",
            aide: "Excluding VAT, everything that comes into your business account.",
            libelle: "Annual turnover",
            suivant: "Continue",
        },
        charges: {
            titre: "And how much do you spend to work?",
            aide: "Equipment, software, advertising, subcontracting, travel, premises: everything the micro-entreprise does not let you deduct.",
            libelle: "Annual business expenses",
            part: "That is {part} of your turnover.",
            suivant: "Continue",
        },
        foyer: {
            titre: "Who is in your tax household?",
            aide: "Your income tax depends on your whole household, and it weighs in the comparison.",
            situation: "Situation",
            seul: "Single",
            couple: "Couple",
            enfants: "Dependent children",
            suivant: "Continue",
        },
        autresRevenus: {
            titre: "Is there other income in your household?",
            aide: "A salary, a pension, your partner's income. We only count the extra tax your business makes you pay.",
            libelle: "Other taxable household income",
            aucun: "No other income",
            suivant: "See when to leave the micro",
        },
        resultat: {
            titre: "Leaving the micro",
            dejaTitre: "Leaving the micro would already gain you {gain} a year",
            dejaDetail:
                "At {ca} of turnover, you gain a thousand euros by switching from {seuil} of expenses, and you are at {charges}. The regime that leaves you the most is {regime}.",
            resterTitre: "Stay in the micro while your expenses stay below {seuil}",
            resterDetail: "That is your threshold at {ca} of turnover, or {part} of what you collect. Today, you are at {charges}.",
            jamaisTitre: "The micro remains your best regime",
            jamaisDetail:
                "At {ca} of turnover, even if you spent everything you collect, no other regime would gain you a thousand euros more.",
            avenirSeuil:
                "If your expenses keep the same share as your business grows, leaving the micro will start paying off from {seuil} of turnover.",
            avenirJamais:
                "If your expenses keep the same share as your business grows, the micro will stay ahead up to its ceiling of {plafond}.",
            regimes: {
                reel: "the sole trader on actual expenses",
                eurl: "the EURL",
                sasu: "the SASU",
            },
            courbe: {
                titre: "Your expense threshold, by turnover",
                aide: "Above the curve, leaving the micro gains you at least a thousand euros a year. Below it, stay. The dot is you.",
                au: "Leaving pays",
                sous: "Stay",
                tva: "VAT",
            },
            reperes: {
                titre: "Not to be confused with your threshold",
                abattement:
                    "Your flat-rate allowance is worth {abattement}. Your threshold comes well before, because under the micro you pay contributions on everything you collect, expenses included.",
                tva: "VAT arrives at {tva} of turnover, whatever your regime: it does not tell you when to leave the micro.",
                plafond: "The micro ceiling is {plafond}, and you must exceed it two years running to leave the scheme.",
            },
            detailLien: "See the detail of the four regimes",
            societeLien: "Salary or dividends, in a company",
            hypotheses: {
                titre: "For this calculation, we assumed that:",
                items: [
                    "your expenses keep the same share of your turnover as it grows;",
                    "in a company, you pay yourself everything it can pay you, with no dividends;",
                    "accountant's fees and chamber levies are not counted;",
                    "the tax counted is what your business adds to your household, not the household's whole tax.",
                ],
            },
            avertissement:
                "These amounts are indicative. Before changing regime, talk it over with an accountant: the change also affects your social cover.",
            recommencer: "Start again",
        },
    },
};

/**
 * La courbe du seuil de dépenses : chiffre d'affaires en abscisse, part des
 * dépenses en ordonnée. La zone au-dessus se colore, le lecteur est un point.
 */
function CourbeSeuil({ courbe, plafond, seuilTva, lecteur, textes, lang }) {
    const L = 320;
    const H = 150;
    const g = 8;
    const d = 8;
    const haut = 10;
    const bas = 22;
    const partMax = Math.min(
        1,
        Math.max(0.4, lecteur.part + 0.1, ...courbe.map((p) => (p.part === null ? 0 : p.part + 0.1))),
    );
    const x = (ca) => g + (Math.min(ca, plafond) / plafond) * (L - g - d);
    const y = (part) => H - bas - (Math.min(part, partMax) / partMax) * (H - haut - bas);
    const points = courbe.filter((p) => p.part !== null);
    const trace = points.map((p, i) => `${i ? "L" : "M"}${x(p.chiffreAffaires).toFixed(1)},${y(p.part).toFixed(1)}`).join(" ");
    const zone = points.length
        ? `${trace} L${x(points[points.length - 1].chiffreAffaires).toFixed(1)},${y(partMax)} L${x(points[0].chiffreAffaires).toFixed(1)},${y(partMax)} Z`
        : "";

    return (
        <div className="mt-6">
            <div className="text-sm font-semibold dark:text-white">{textes.titre}</div>
            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1 mb-2">{textes.aide}</p>
            <svg viewBox={`0 0 ${L} ${H}`} className="w-full h-auto" aria-hidden="true">
                {zone && <path d={zone} className="fill-primary/10" />}
                {trace && <path d={trace} fill="none" stroke="currentColor" strokeWidth="2" className="text-primary" />}
                <text x={g + 4} y={haut + 10} className="fill-primary text-[9px] font-semibold">
                    {textes.au}
                </text>
                <text x={L - d - 4} y={H - bas - 6} textAnchor="end" className="fill-gray-400 text-[9px]">
                    {textes.sous}
                </text>
                {seuilTva < plafond && (
                    <>
                        <line
                            x1={x(seuilTva)}
                            x2={x(seuilTva)}
                            y1={haut}
                            y2={H - bas}
                            stroke="currentColor"
                            strokeDasharray="3 3"
                            className="text-gray-300 dark:text-white/20"
                        />
                        <text x={x(seuilTva) + 3} y={H - bas - 6} className="fill-gray-400 text-[9px]">
                            {textes.tva}
                        </text>
                    </>
                )}
                <line x1={g} x2={L - d} y1={H - bas} y2={H - bas} stroke="currentColor" className="text-gray-300 dark:text-white/20" />
                <circle cx={x(lecteur.chiffreAffaires)} cy={y(lecteur.part)} r="5" className="fill-gray-900 dark:fill-white" />
                <text x={g} y={H - 6} className="fill-gray-400 text-[9px]">
                    0
                </text>
                <text x={L - d} y={H - 6} textAnchor="end" className="fill-gray-400 text-[9px]">
                    {euros(plafond, lang)}
                </text>
            </svg>
        </div>
    );
}

export default function QuandQuitterLaMicro({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const r = t.resultat;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    const { valeurs: dossier } = useDossierOutil();
    const [activite, setActivite] = useState(dossier.activite ?? null);
    const [chiffreAffaires, setChiffreAffaires] = useState(dossier.chiffreAffaires ?? 45000);
    const [charges, setCharges] = useState(dossier.charges ?? 5000);
    const [enCouple, setEnCouple] = useState(dossier.enCouple ?? false);
    const [enfants, setEnfants] = useState(dossier.enfants ?? 0);
    const [autresRevenus, setAutresRevenus] = useState(dossier.autresRevenus ?? 0);

    const { etape, communs, rang, aller, retour, recommencer } = useParcours(
        "activite",
        () => {
            setActivite(null);
            setChiffreAffaires(45000);
            setCharges(5000);
            setEnCouple(false);
            setEnfants(0);
            setAutresRevenus(0);
        },
        PHASES,
        reprendre([
            { etape: "activite", connu: dossier.activite !== undefined },
            // Un chiffre hors des bornes du curseur (zéro pour un projet) se redemande.
            {
                etape: "chiffreAffaires",
                connu: dossier.chiffreAffaires >= 5000 && dossier.chiffreAffaires <= (dossier.activite === "vente" ? 203000 : 83000),
            },
            { etape: "charges", connu: dossier.charges !== undefined },
            { etape: "foyer", connu: dossier.enCouple !== undefined && dossier.enfants !== undefined },
            { etape: "autresRevenus", connu: dossier.autresRevenus !== undefined },
            { etape: "resultat" },
        ]),
    );

    useSignalerDossier(
        { activite, chiffreAffaires, charges, enCouple, enfants, autresRevenus },
        etape === "resultat",
    );

    const parametres = useMemo(
        () => ({
            chiffreAffaires,
            charges: Math.min(charges, chiffreAffaires),
            activite: activite || "services",
            enCouple,
            enfants,
            autresRevenus,
        }),
        [chiffreAffaires, charges, activite, enCouple, enfants, autresRevenus],
    );

    const calcul = useMemo(
        () => (etape === "resultat" ? quandQuitterLaMicro(parametres) : null),
        [etape, parametres],
    );

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };
    const sommes = (v) => euros(Math.round(v), lang);
    // Le gain annoncé dit d'où il vient : la micro, le meilleur régime, l'écart.
    const ex = useMemo(
        () =>
            calcul
                ? expliquerQuitterMicro(
                      calcul.aujourdhui,
                      {
                          chiffreAffaires: parametres.chiffreAffaires,
                          charges: parametres.charges,
                          activite: parametres.activite,
                          artisan: false,
                          foyer: { enCouple, enfants, parentIsole: false, autresRevenus },
                      },
                      lang,
                  )
                : null,
        [calcul, parametres, enCouple, enfants, autresRevenus, lang],
    );
    const { ouverte, basculer } = useExplications();

    if (etape === "activite") {
        return (
            <Question
                key="activite"
                {...communsEcran}
                phase={PHASES.activite}
                rang={rang}
                titre={t.activite.titre}
                aide={t.activite.aide}
                choix={t.activite.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        setActivite(c.cle);
                        aller("chiffreAffaires");
                    },
                }))}
            />
        );
    }

    if (etape === "chiffreAffaires") {
        return (
            <Ecran
                key="chiffreAffaires"
                {...communsEcran}
                phase={PHASES.chiffreAffaires}
                rang={rang}
                titre={t.chiffreAffaires.titre}
                aide={t.chiffreAffaires.aide}
            >
                <Curseur
                    libelle={t.chiffreAffaires.libelle}
                    valeur={chiffreAffaires}
                    poser={setChiffreAffaires}
                    minimum={5000}
                    maximum={activite === "vente" ? 203000 : 83000}
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("charges")}>{t.chiffreAffaires.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "charges") {
        const part = chiffreAffaires > 0 ? Math.min(charges, chiffreAffaires) / chiffreAffaires : 0;
        return (
            <Ecran
                key="charges"
                {...communsEcran}
                phase={PHASES.charges}
                rang={rang}
                titre={t.charges.titre}
                aide={t.charges.aide}
            >
                <Curseur
                    libelle={t.charges.libelle}
                    aide={remplir(t.charges.part, { part: pourcent(part, lang, 0) })}
                    valeur={Math.min(charges, chiffreAffaires)}
                    poser={setCharges}
                    minimum={0}
                    maximum={chiffreAffaires}
                    pas={500}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("foyer")}>{t.charges.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "foyer") {
        return (
            <Ecran
                key="foyer"
                {...communsEcran}
                phase={PHASES.foyer}
                rang={rang}
                titre={t.foyer.titre}
                aide={t.foyer.aide}
            >
                <div className="space-y-5">
                    <div>
                        <div className="text-sm text-gray-600 dark:text-lightBlue mb-2">
                            {t.foyer.situation}
                        </div>
                        <Segments
                            options={[
                                { cle: "seul", libelle: t.foyer.seul },
                                { cle: "couple", libelle: t.foyer.couple },
                            ]}
                            valeur={enCouple ? "couple" : "seul"}
                            poser={(v) => setEnCouple(v === "couple")}
                        />
                    </div>
                    <Curseur
                        libelle={t.foyer.enfants}
                        valeur={enfants}
                        poser={setEnfants}
                        minimum={0}
                        maximum={6}
                        pas={1}
                        formater={(v) => String(v)}
                    />
                </div>
                <Avancer onClick={() => aller("autresRevenus")}>{t.foyer.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "autresRevenus") {
        return (
            <Ecran
                key="autresRevenus"
                {...communsEcran}
                phase={PHASES.autresRevenus}
                rang={rang}
                titre={t.autresRevenus.titre}
                aide={t.autresRevenus.aide}
            >
                <Curseur
                    libelle={t.autresRevenus.libelle}
                    aide={autresRevenus === 0 ? t.autresRevenus.aucun : undefined}
                    valeur={autresRevenus}
                    poser={setAutresRevenus}
                    minimum={0}
                    maximum={120000}
                    saisie
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("resultat")}>{t.autresRevenus.suivant}</Avancer>
            </Ecran>
        );
    }

    const { aujourdhui, depensesUtiles, dejaPasse, seuil, plafond, seuilTva, part, courbe } = calcul;
    const valeurs = {
        ca: sommes(parametres.chiffreAffaires),
        charges: sommes(parametres.charges),
        seuil: depensesUtiles === null ? "" : sommes(depensesUtiles),
        part: depensesUtiles === null ? "" : pourcent(depensesUtiles / parametres.chiffreAffaires, lang, 0),
    };

    const titre =
        depensesUtiles === null
            ? r.jamaisTitre
            : dejaPasse
              ? phraseAvecMontants(r.dejaTitre, {
                    gain: (
                        <MontantExplicable explication={ex.gain} ouvert={ouverte === "gain"} onBasculer={() => basculer("gain")}>
                            {sommes(aujourdhui.gainSiChangement)}
                        </MontantExplicable>
                    ),
                })
              : remplir(r.resterTitre, valeurs);
    const detail =
        depensesUtiles === null
            ? remplir(r.jamaisDetail, valeurs)
            : dejaPasse
              ? remplir(r.dejaDetail, { ...valeurs, regime: r.regimes[aujourdhui.meilleurRegime] })
              : remplir(r.resterDetail, valeurs);

    return (
        <Resultat onRetour={retour} retour={t.retour} key="resultat" titre={r.titre} phases={t.phases} {...communs}>
            <div className="mt-2 font-bold text-2xl leading-tight dark:text-white">{titre}</div>
            {ouverte === "gain" && <PanneauExplication explication={ex.gain} lang={lang} />}
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{detail}</p>
            {!dejaPasse && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                    {seuil !== null
                        ? remplir(r.avenirSeuil, { seuil: sommes(seuil) })
                        : remplir(r.avenirJamais, { plafond: sommes(plafond) })}
                </p>
            )}

            <CourbeSeuil
                courbe={courbe}
                plafond={plafond}
                seuilTva={seuilTva}
                lecteur={{ chiffreAffaires: parametres.chiffreAffaires, part }}
                textes={r.courbe}
                lang={lang}
            />

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.reperes.titre}</div>
                <Liste
                    items={[
                        remplir(r.reperes.abattement, {
                            abattement: sommes(aujourdhui.abattementForfaitaire),
                        }),
                        remplir(r.reperes.tva, { tva: sommes(seuilTva) }),
                        remplir(r.reperes.plafond, { plafond: sommes(plafond) }),
                    ]}
                />
            </div>

            {/* Le détail des quatre régimes vit dans le comparateur, et le
                partage d'une société dans son propre outil : on y renvoie
                plutôt que de tout répéter ici. */}
            <div className="mt-6 grid gap-2 sm:grid-cols-2">
                <Link
                    href={{
                        pathname: "/tools/[slug]",
                        params: { slug: lang === "fr" ? "micro-reel-ou-societe" : "micro-sole-trader-or-company" },
                    }}
                >
                    <Button type="primary" className="press-safe w-full">
                        {r.detailLien} →
                    </Button>
                </Link>
                <Link
                    href={{
                        pathname: "/tools/[slug]",
                        params: { slug: lang === "fr" ? "remuneration-ou-dividendes" : "salary-or-dividends" },
                    }}
                >
                    <Button type="secondary" className="press-safe w-full">
                        {r.societeLien} →
                    </Button>
                </Link>
            </div>

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.hypotheses.titre}</div>
                <Liste items={r.hypotheses.items} />
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0 leading-relaxed">
                {r.avertissement}
            </p>

            <Recommencer onClick={recommencer} libelle={r.recommencer} />
        </Resultat>
    );
}
